import { useEffect } from 'react';
import { useSearchParams as useRouterSearchParams } from 'react-router-dom';
import { anchorRef, bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { Box, Stack } from '@mui/material';

import { ContainerColumnLeft } from '@occc-app/design-system';
import {
  FilterButton,
  isFiltersSelected,
  SearchForm,
  SearchFormProps,
  TableContainer,
  TableHeader,
  Tab,
  Tabs,
} from '@occc/ui-design-system';
import {
  MembershipActivityFragment,
  MembershipStateChangeFragment,
  Point_Expiration_Strategy,
} from '@occc/graphql';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { useTabQueryParam } from '@occc/utils-query-params';
import { useDateQueryParam } from '@occc-app/routes/Transactions/TransactionsListPage/hooks';
import { useChainConfiguration } from '@occc-app/Chain/hooks/useChainConfiguration';
import { useConfig } from '@occc/config';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { PointExpirationScheduleController } from '@occc/feat-point-expiration-schedule';
import { StoredValueExpirationComponent } from '@occc/feat-stored-value';
import { MembershipActivityContainer } from './membership-activity';
import { useTypeFilter } from './membership-activity/membership-activity.hooks';
import StateChangesTable from './StateChanges/StateChangesTable';
import { ActivityTab } from './types';
import {
  ACTIVITY_TAB_PARAM,
  ACTIVITY_TABS,
  DEFAULT_ACTIVITY_TAB,
  LEGACY_ACTIVITY_TAB,
} from './constants';

import {
  POINT_EXPIRATIONS_END_DATE_PARAM,
  POINT_EXPIRATIONS_START_DATE_PARAM,
  TRANSACTIONS_END_DATE_PARAM,
  TRANSACTIONS_PARAM_NAMES,
  TRANSACTIONS_START_DATE_PARAM,
} from '../../constants';

type MemberDetailsActivityProps = {
  memberId: string;
  stateChanges: MembershipStateChangeFragment[];
  onMembershipActivityClick(item: MembershipActivityFragment): void;
};

const transitionStyle = 'transform 500ms cubic-bezier(.75, 0, .25, 1)';
const programDetailsBoxStyles = {
  transition: transitionStyle,
  overflow: 'hidden',
  width: '100%',
};

const activityCommonStyles = (isActive: boolean) => ({
  transition: transitionStyle,
  width: '100%',
  overflow: 'hidden',
  maxHeight: isActive ? '100%' : '0px',
});

const transactionStyles = (isActive: boolean) => ({
  ...activityCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(-100%, 0)',
});

const stateChangesStyles = (isActive: boolean) => ({
  ...activityCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(100%, 0)',
});

const pointExpirationsStyles = (isActive: boolean) => ({
  ...activityCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(200%, 0)',
});

const storedValueExpirationsStyles = (isActive: boolean, hasPointExpirations: boolean) => ({
  ...activityCommonStyles(isActive),
  transform: isActive
    ? 'translate(0, 0)'
    : `translate(${hasPointExpirations ? '400%' : '300%'}, 0)`,
});

const Activity = ({
  memberId,
  onMembershipActivityClick,
  stateChanges,
}: MemberDetailsActivityProps) => {
  const chainConfiguration = useChainConfiguration();
  const { config } = useConfig();
  const hasPointExpirations =
    chainConfiguration.pointExpiration.strategy === Point_Expiration_Strategy.FirstInFirstOut;
  const hasStoredValueExpirationTab = config.storedValue.enabled;

  const [activeTab, setActiveTab] = useTabQueryParam<ActivityTab>(
    ACTIVITY_TAB_PARAM,
    DEFAULT_ACTIVITY_TAB,
    ACTIVITY_TABS,
  );
  const [routerSearchParams, setRouterSearchParams] = useRouterSearchParams();

  useEffect(() => {
    if (routerSearchParams.get(ACTIVITY_TAB_PARAM) !== LEGACY_ACTIVITY_TAB) {
      return;
    }

    const nextParams = new URLSearchParams(routerSearchParams);
    nextParams.delete(ACTIVITY_TAB_PARAM);
    setRouterSearchParams(nextParams, { replace: true });
  }, [routerSearchParams, setRouterSearchParams]);

  useEffect(() => {
    if (activeTab === 'pointExpirations' && !hasPointExpirations) {
      setActiveTab('transactions');
    }
  }, [activeTab, hasPointExpirations, setActiveTab]);

  useEffect(() => {
    if (activeTab === 'storedValueExpirations' && !hasStoredValueExpirationTab) {
      setActiveTab('transactions');
    }
  }, [activeTab, hasStoredValueExpirationTab, setActiveTab]);

  const [transactionsType] = useTypeFilter();
  const [transactionsStartDate] = useDateQueryParam(TRANSACTIONS_START_DATE_PARAM);
  const [transactionsEndDate] = useDateQueryParam(TRANSACTIONS_END_DATE_PARAM);
  const [pointExpirationsStartDate] = useDateQueryParam(POINT_EXPIRATIONS_START_DATE_PARAM);
  const [pointExpirationsEndDate] = useDateQueryParam(POINT_EXPIRATIONS_END_DATE_PARAM);

  const showBadgeTransactions = isFiltersSelected(
    [transactionsType, transactionsStartDate, transactionsEndDate],
    ['any'],
  );
  const showBadgePointExpirations = isFiltersSelected([
    pointExpirationsStartDate,
    pointExpirationsEndDate,
  ]);
  const showBadge =
    (activeTab === 'transactions' && showBadgeTransactions) ||
    (activeTab === 'pointExpirations' && showBadgePointExpirations);

  const handleTabChange = (_event: React.SyntheticEvent<Element, Event>, value: ActivityTab) => {
    setActiveTab(value);
  };

  const [transactionsSearch, setTransactionsSearch] = useSearchParams(TRANSACTIONS_PARAM_NAMES);

  const popupState = usePopupState({ variant: 'popover', popupId: 'filters' });
  const transactionsPopoverProps = {
    ...bindPopover({
      ...popupState,
      isOpen: popupState.isOpen && activeTab === 'transactions',
    }),
  };

  const onSearchSubmit: SearchFormProps['onSubmit'] = ({ search: value }) => {
    if (activeTab === 'transactions') {
      setTransactionsSearch(value);
    }
  };

  const renderActions = () => {
    if (
      activeTab === 'stateChanges' ||
      activeTab === 'pointExpirations' ||
      activeTab === 'storedValueExpirations'
    ) {
      return null;
    }

    return (
      <Stack direction='row' alignItems='center' gap={1}>
        <SearchForm
          onSubmit={onSearchSubmit}
          initialValues={{
            search: transactionsSearch || '',
          }}
        />
        <FilterButton
          {...bindTrigger(popupState)}
          ref={anchorRef(popupState)}
          showBadge={showBadge}
        />
      </Stack>
    );
  };

  return (
    <ContainerColumnLeft>
      <TableContainer sx={{ boxShadow: 'none' }}>
        <TableHeader
          tabs={
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab value={'transactions' satisfies ActivityTab} label='Transactions' />
              <Tab value={'stateChanges' satisfies ActivityTab} label='State Changes' />
              {hasPointExpirations && (
                <Tab
                  value={'pointExpirations' satisfies ActivityTab}
                  label='Upcoming Point Expirations'
                />
              )}
              {hasStoredValueExpirationTab && (
                <Tab
                  value={'storedValueExpirations' satisfies ActivityTab}
                  label='Stored Value Expiration'
                />
              )}
            </Tabs>
          }
          actions={renderActions()}
        />
        <Box style={programDetailsBoxStyles}>
          <Box
            id={'transactions' satisfies ActivityTab}
            style={transactionStyles(activeTab === 'transactions')}
          >
            <MembershipActivityContainer
              memberId={memberId}
              onMembershipActivityClick={onMembershipActivityClick}
              searchText={transactionsSearch}
              popoverProps={transactionsPopoverProps}
            />
          </Box>
          <Box
            id={'stateChanges' satisfies ActivityTab}
            style={stateChangesStyles(activeTab === 'stateChanges')}
          >
            <StateChangesTable stateChanges={stateChanges} />
          </Box>
          {hasPointExpirations && (
            <Box
              id={'pointExpirations' satisfies ActivityTab}
              style={pointExpirationsStyles(activeTab === 'pointExpirations')}
            >
              <PointExpirationScheduleController memberId={memberId} />
            </Box>
          )}
          {hasStoredValueExpirationTab && (
            <Box
              id={'storedValueExpirations' satisfies ActivityTab}
              style={storedValueExpirationsStyles(
                activeTab === 'storedValueExpirations',
                hasPointExpirations,
              )}
            >
              <StoredValueExpirationComponent memberId={memberId} />
            </Box>
          )}
        </Box>
      </TableContainer>
    </ContainerColumnLeft>
  );
};

export default Activity;
